// Pure helper for the dashboard's inferred verification level. The
// real verifier (src/lib/verify.ts) runs cryptographic checks; the
// dashboard cannot, so this helper looks only at presence of the
// fields that drive each flag in the verifier. The level returned
// here can never overstate the verifier's output, because every
// additional signature check the verifier runs can only fail (and
// downgrade the level), never succeed in ways the presence check
// missed.
//
// v0.6 arc: the level is the identity-anchored base only:
//   L0 = base
//   L1 = witnessed (witnessed-only fallback)
//   L2 = agent identity bound (does NOT require witnessed)
//   L3 = agent identity bound AND policy bound
// Approval (old L4) and counterparty (old L5) are NOT ladder rungs —
// they are independent badges, like witnessed. Use hasApproval() /
// hasCounterparty() for those, rendered as separate badges.
//
// "Witnessed" requires one witness attestation per action so an
// envelope cannot reach L1 by stuffing a stray attestation at the
// front of the chain. "Agent identity bound" applies the same rule
// to agent attestations.

import type { ReceiptEnvelope } from "../api/client";

export type InferredLevel =
  | "L0_INTEGRITY_ONLY"
  | "L1_WITNESSED"
  | "L2_KEY_BOUND"
  | "L3_POLICY_BOUND";

const LEVEL_LABELS: Record<InferredLevel, string> = {
  L0_INTEGRITY_ONLY: "L0 integrity only",
  L1_WITNESSED: "L1 witnessed",
  L2_KEY_BOUND: "L2 key bound",
  L3_POLICY_BOUND: "L3 policy bound"
};

// True when the attestation sequences cover every action in the chain
// exactly, from chain.sequence_start through sequence_start + actionCount
// - 1. The verifier locates each per-action attestation by sequence
// (src/lib/verify.ts: find((a) => a.sequence === action.sequence)), so a
// count-only check would overstate: attestations that duplicate one
// sequence and omit another match on length but leave an action
// unattested, and the verifier sets the flag false. Requiring exact
// per-sequence coverage mirrors that. Combined with the count guard this
// means exactly one attestation per action sequence.
function coversEverySequence(
  sequences: number[],
  sequenceStart: number,
  actionCount: number
): boolean {
  if (actionCount <= 0) return false;
  if (sequences.length !== actionCount) return false;
  const present = new Set(sequences);
  for (let seq = sequenceStart; seq < sequenceStart + actionCount; seq++) {
    if (!present.has(seq)) return false;
  }
  return true;
}

// A receipt is witnessed only when every action carries a witness
// attestation, matching the verifier's flags.witnessed rule
// (src/lib/verify.ts: witnessSignaturesOk && count === actions.length).
// Coverage is checked by action sequence, not just total count, so a
// chain that duplicates one sequence and omits another is not reported
// as witnessed. This is the dashboard's presence-based view; the real
// verifier additionally checks each signature and can only downgrade.
export function isWitnessed(envelope: ReceiptEnvelope): boolean {
  return coversEverySequence(
    envelope.witness_attestations.map((att) => att.sequence),
    envelope.chain.sequence_start,
    envelope.action_record_hashes.length
  );
}

export function inferVerificationLevel(envelope: ReceiptEnvelope): InferredLevel {
  const witnessed = isWitnessed(envelope);
  const agentIdentityBound = coversEverySequence(
    envelope.agent_attestations.map((att) => att.sequence),
    envelope.chain.sequence_start,
    envelope.action_record_hashes.length
  );
  const policyBound = Boolean(envelope.task.policy_context_hash);

  if (agentIdentityBound && policyBound) {
    return "L3_POLICY_BOUND";
  }
  if (agentIdentityBound) {
    return "L2_KEY_BOUND";
  }
  if (witnessed) {
    return "L1_WITNESSED";
  }
  return "L0_INTEGRITY_ONLY";
}

export function formatInferredLevel(level: InferredLevel): string {
  return `${LEVEL_LABELS[level]} (pending verifier check)`;
}

// Display labels for the verification-level badge. Per protocol-spec.md
// 6.3, witnessed is an independent axis and the level is a single ladder
// tier; the dashboard renders them as two separate badges (PR C of the
// verification-level-semantics arc) rather than collapsing witnessing
// into one ordinal label. NONE is the verifier's hard-failure state and
// maps to "Failed"; the inferred level never reaches NONE, but the badge
// helper covers it so a report-driven caller can reuse the same mapping.
export type DisplayLevel = InferredLevel | "NONE";

const LEVEL_BADGE_LABELS: Record<DisplayLevel, string> = {
  L0_INTEGRITY_ONLY: "L0 Integrity Only",
  L1_WITNESSED: "L1 Witnessed",
  L2_KEY_BOUND: "L2 Key Bound",
  L3_POLICY_BOUND: "L3 Policy Bound",
  NONE: "Failed"
};

export function levelBadgeLabel(level: DisplayLevel): string {
  return LEVEL_BADGE_LABELS[level];
}

// Witnessed-badge label. Both states are user-visible: a not-witnessed
// receipt shows "Not witnessed" rather than hiding the badge, so the
// absence of witnessing is explicit rather than inferred from missing UI.
export function witnessedBadgeLabel(witnessed: boolean): string {
  return witnessed ? "Witnessed" : "Not witnessed";
}

// v0.6 arc: approval and counterparty are independent badges (presence
// from the envelope; the real verifier additionally checks signatures
// and identity vouching, and can only downgrade). Identity is not yet
// vouched, so these are presence-only at the dashboard's altitude.
export function hasApproval(envelope: ReceiptEnvelope): boolean {
  return envelope.approval_attestations.length > 0;
}

export function hasCounterparty(envelope: ReceiptEnvelope): boolean {
  return (envelope.counterparty_attestations ?? []).length > 0;
}
